import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, X, Lock, ClipboardCheck, Play, FileSignature, Printer, AlertTriangle, ChevronRight, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLOR = {
  draft: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-amber-100 text-amber-700',
  locked: 'bg-emerald-100 text-emerald-700',
};

const AuditsPage = () => {
  const { api, user } = useAuth();
  const [audits, setAudits] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', description: '', start_date: '', end_date: '', scope_categories: [], scope_location_ids: [], scope_item_ids: [] });
  const [active, setActive] = useState(null); // active audit detail
  const [activeReport, setActiveReport] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signature, setSignature] = useState('');
  const [applyAdjustments, setApplyAdjustments] = useState(true);

  const isAdmin = user?.role === 'Admin';
  const canManage = isAdmin || user?.role === 'InventoryManager';

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [aR, iR, cR, lR] = await Promise.all([
        api().get('/audits'),
        api().get('/items'),
        api().get('/item-categories').catch(() => ({ data: [] })),
        api().get('/locations').catch(() => ({ data: [] })),
      ]);
      setAudits(aR.data || []);
      setItems(iR.data || []);
      setCategories(cR.data || []);
      setLocations(lR.data || []);
    } catch (e) { toast.error('Failed to load audits'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = tab === 'all' ? audits : audits.filter(a => a.status === tab);

  const create = async () => {
    if (!createForm.title.trim()) { toast.error('Title required'); return; }
    try {
      await api().post('/audits', createForm);
      toast.success('Audit created');
      setCreating(false);
      setCreateForm({ title: '', description: '', start_date: '', end_date: '', scope_categories: [], scope_location_ids: [], scope_item_ids: [] });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startAudit = async (a) => {
    if (!window.confirm(`Start audit "${a.title}"? This will snapshot current quantities for all in-scope items.`)) return;
    try {
      const r = await api().post(`/audits/${a.id}/start`);
      toast.success(`Audit started (${r.data.lines} lines)`);
      fetchAll();
      openDetail(a.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openDetail = async (aid) => {
    try {
      const [d, rep] = await Promise.all([
        api().get(`/audits/${aid}`),
        api().get(`/audits/${aid}/reconciliation-report`).catch(() => ({ data: null })),
      ]);
      setActive(d.data);
      setActiveReport(rep.data);
    } catch (e) { toast.error('Failed to load audit'); }
  };

  const countLine = async (line, counted) => {
    try {
      await api().post(`/audits/${active.id}/lines/${line.id}/count`, { counted_qty: parseFloat(counted) });
      openDetail(active.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Count failed'); }
  };

  const completeAudit = async () => {
    if (!window.confirm('Complete the audit? After completion, you can lock it for reconciliation.')) return;
    try {
      await api().post(`/audits/${active.id}/complete`, { reconciliation_notes: '' });
      toast.success('Audit completed');
      fetchAll();
      openDetail(active.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const lockAudit = async () => {
    try {
      await api().post(`/audits/${active.id}/lock`, {
        signature_data: signature.trim(),
        apply_adjustments: applyAdjustments,
      });
      toast.success(`Audit locked${applyAdjustments ? ' & adjustments applied' : ''}`);
      setSigning(false);
      setSignature('');
      fetchAll();
      openDetail(active.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteAudit = async (a) => {
    if (!window.confirm(`Delete audit "${a.title}"?`)) return;
    try {
      await api().delete(`/audits/${a.id}`);
      toast.success('Deleted');
      fetchAll();
      if (active?.id === a.id) setActive(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const toggleScopeCategory = (c) => setCreateForm(f => ({ ...f, scope_categories: f.scope_categories.includes(c) ? f.scope_categories.filter(x => x !== c) : [...f.scope_categories, c] }));
  const toggleScopeLocation = (l) => setCreateForm(f => ({ ...f, scope_location_ids: f.scope_location_ids.includes(l) ? f.scope_location_ids.filter(x => x !== l) : [...f.scope_location_ids, l] }));

  // iter68 — branded printable audit reconciliation report
  const printReport = async (mode = 'letter') => {
    if (!active || !activeReport) return;
    const { openBrandedPrintWindow, PrintHelpers: H } = await import('../utils/brandedPrint');
    const a = active;
    const t = activeReport.totals;
    const kpiBoxes = [
      ['Lines', t.lines],
      ['With Variance', t.with_variance],
      ['Total Δ Qty', t.total_variance_qty],
      ['Total Δ Value', `$${t.total_variance_value}`],
    ];
    const kpiHtml = `<div class="grid-2" style="grid-template-columns: repeat(4, 1fr); gap: 10pt;">${kpiBoxes.map(([l, v]) => `
      <div style="border:1px solid #e2e8f0; padding:10pt; border-radius:4px; background:#f8fafc; text-align:center;">
        <div style="font-size:18pt; font-weight:800; color:#0f172a;">${v}</div>
        <div class="label">${H.escape(l)}</div>
      </div>
    `).join('')}</div>`;

    const cols = [
      { key: 'item_sku', label: 'SKU', mono: true },
      { key: 'item_name', label: 'Item' },
      { key: 'expected_qty', label: 'Expected', mono: true },
      { key: 'counted_qty', label: 'Counted', mono: true },
      { key: 'variance', label: 'Δ Qty', mono: true, fmt: (v) => v != null ? `<span style="color:${v < 0 ? '#b91c1c' : v > 0 ? '#047857' : '#475569'}">${v}</span>` : '—' },
      { key: 'variance_value', label: 'Δ Value', mono: true, fmt: (v) => v != null ? `<span style="color:${v < 0 ? '#b91c1c' : v > 0 ? '#047857' : '#475569'}">$${v.toFixed(2)}</span>` : '—' },
      { key: 'counted_by_name', label: 'Counted by', fmt: (_, r) => `${r.counted_by_name || ''}<br/><span style="font-size:8pt; color:#888;">${r.counted_at ? new Date(r.counted_at).toLocaleString() : ''}</span>` },
    ];

    await openBrandedPrintWindow({
      title: 'AUDIT RECONCILIATION',
      subtitle: `${a.audit_number} · ${a.title || ''}`,
      orientation: mode === 'label' ? 'portrait' : 'landscape',
      pageSize: mode === 'label' ? '4in 6in' : 'Letter',
      statusBadge: H.statusBadge(a.status, a.status === 'signed' ? '#059669' : a.status === 'in_progress' ? '#d97706' : '#475569'),
      body: `${H.callout(`<strong>${H.escape(a.start_date || '')}</strong> → <strong>${H.escape(a.end_date || '')}</strong>`)}${kpiHtml}${H.sectionTitle('Audit Lines')}${H.table(cols, a.audit_lines || [])}${a.reconciliation_notes ? `${H.sectionTitle('Reconciliation Notes')}<div class="value">${H.escape(a.reconciliation_notes)}</div>` : ''}${a.signed_at ? H.signoffLines(`Signed by ${a.signed_by_name || ''}`, new Date(a.signed_at).toLocaleString()) : H.signoffLines('Auditor signature', 'Date')}`,
      footerNote: 'Internal Document · Inventory Audit',
    });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  // Audit detail view
  if (active) {
    const a = active;
    const t = activeReport?.totals || { lines: 0, counted: 0, with_variance: 0, total_variance_qty: 0, total_variance_value: 0 };
    return (
      <div className="space-y-4 animate-slideIn pb-12" data-testid="audit-detail">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setActive(null)} data-testid="audit-back-btn">← All Audits</Button>
          <div className="flex gap-2">
            {a.status === 'draft' && canManage && <Button onClick={() => startAudit(a)} className="bg-blue-600 hover:bg-blue-700" data-testid="audit-start-btn"><Play className="w-4 h-4 mr-1" /> Start Audit</Button>}
            {a.status === 'in_progress' && canManage && <Button onClick={completeAudit} className="bg-amber-600 hover:bg-amber-700" data-testid="audit-complete-btn"><ClipboardCheck className="w-4 h-4 mr-1" /> Complete</Button>}
            {a.status === 'completed' && isAdmin && <Button onClick={() => setSigning(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="audit-lock-btn"><Lock className="w-4 h-4 mr-1" /> Lock & Sign</Button>}
            <Button variant="outline" onClick={() => printReport('letter')} data-testid="audit-print-btn"><Printer className="w-4 h-4 mr-1" /> Print Letter</Button>
            <Button variant="outline" onClick={() => printReport('label')} data-testid="audit-print-label-btn"><Printer className="w-4 h-4 mr-1" /> Print 4×6 Label</Button>
          </div>
        </div>

        <Card className="industrial-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <h1 className="text-2xl font-bold text-slate-900">{a.title}</h1>
              <span className={`text-xs px-2 py-0.5 rounded-sm ${STATUS_COLOR[a.status]}`}>{a.status}</span>
              <span className="text-xs text-slate-400 mono">{a.audit_number}</span>
              {a.locked_at && <span className="ml-auto text-xs text-emerald-700 flex items-center gap-1"><Lock className="w-3 h-3" /> Locked {new Date(a.locked_at).toLocaleDateString()}</span>}
            </div>
            {a.description && <p className="text-sm text-slate-600 mb-3">{a.description}</p>}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border border-slate-200 rounded-sm p-3"><div className="text-xs uppercase text-slate-500">Lines</div><div className="text-2xl font-bold mono">{t.lines}</div></div>
              <div className="border border-slate-200 rounded-sm p-3"><div className="text-xs uppercase text-slate-500">Counted</div><div className="text-2xl font-bold mono">{t.counted}<span className="text-sm text-slate-400"> / {t.lines}</span></div></div>
              <div className="border border-slate-200 rounded-sm p-3"><div className="text-xs uppercase text-slate-500">With Variance</div><div className="text-2xl font-bold mono text-amber-700">{t.with_variance}</div></div>
              <div className="border border-slate-200 rounded-sm p-3"><div className="text-xs uppercase text-slate-500">Δ Value</div><div className={`text-2xl font-bold mono ${t.total_variance_value < 0 ? 'text-red-600' : 'text-emerald-700'}`}>${t.total_variance_value || 0}</div></div>
            </div>
          </CardContent>
        </Card>

        {a.audit_lines && a.audit_lines.length > 0 && (
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Audit Lines</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Expected</TableHead>
                    <TableHead className="text-right">Counted</TableHead>
                    <TableHead className="text-right">Δ Qty</TableHead>
                    <TableHead className="text-right">Δ Value</TableHead>
                    <TableHead>Counted By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {a.audit_lines.map(ln => {
                    const editable = a.status === 'in_progress';
                    return (
                      <TableRow key={ln.id} className={ln.counted_qty == null ? '' : (ln.variance && Math.abs(ln.variance) > 0 ? 'bg-amber-50' : '')} data-testid={`audit-line-${ln.item_sku}`}>
                        <TableCell className="mono text-xs">{ln.item_sku}</TableCell>
                        <TableCell>{ln.item_name}<br /><span className="text-[10px] text-slate-400">{ln.item_category}</span></TableCell>
                        <TableCell className="text-right mono text-sm">{ln.expected_qty}</TableCell>
                        <TableCell className="text-right">
                          {editable ? (
                            <Input type="number" step="0.01" defaultValue={ln.counted_qty ?? ''} placeholder="—" className="rounded-sm h-8 text-xs mono text-right w-24 ml-auto" onBlur={(e) => e.target.value !== '' && countLine(ln, e.target.value)} data-testid={`audit-count-${ln.item_sku}`} />
                          ) : (
                            <span className="mono text-sm">{ln.counted_qty ?? '—'}</span>
                          )}
                        </TableCell>
                        <TableCell className={`text-right mono text-sm ${ln.variance < 0 ? 'text-red-600' : ln.variance > 0 ? 'text-emerald-700' : ''}`}>{ln.variance ?? '—'}</TableCell>
                        <TableCell className={`text-right mono text-sm ${ln.variance_value < 0 ? 'text-red-600' : ln.variance_value > 0 ? 'text-emerald-700' : ''}`}>{ln.variance_value != null ? `$${ln.variance_value.toFixed(2)}` : '—'}</TableCell>
                        <TableCell className="text-xs text-slate-500">{ln.counted_by_name || '—'}{ln.counted_at && <><br /><span className="text-[10px]">{new Date(ln.counted_at).toLocaleString()}</span></>}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Lock + Sign dialog */}
        {signing && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSigning(false)}>
            <div className="bg-white rounded-sm max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="audit-sign-dialog">
              <div className="p-5 border-b border-slate-200"><h2 className="text-lg font-semibold flex items-center gap-2"><FileSignature className="w-5 h-5 text-emerald-600" /> Lock & Sign Audit</h2></div>
              <div className="p-5 space-y-3">
                <p className="text-sm text-slate-600">This action is irreversible. Your name and timestamp will be recorded.</p>
                <div>
                  <Label>Signature (typed name)</Label>
                  <Input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder={user?.full_name || user?.email} className="rounded-sm" data-testid="audit-signature-input" />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer p-2 bg-amber-50 border border-amber-200 rounded-sm" data-testid="audit-apply-adjustments">
                  <input type="checkbox" checked={applyAdjustments} onChange={(e) => setApplyAdjustments(e.target.checked)} className="accent-emerald-500" />
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <span><span className="font-semibold">Apply variance adjustments</span> to live inventory on lock (recommended).</span>
                </label>
              </div>
              <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSigning(false)}>Cancel</Button>
                <Button onClick={lockAudit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="audit-confirm-lock-btn"><Lock className="w-4 h-4 mr-1" /> Lock & Sign</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slideIn" data-testid="audits-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Audit Process</h1>
          <p className="text-slate-500 text-sm">Formal multi-day audits with snapshot, line-by-line counting, signed reconciliation, and optional inventory adjustments. Restricted to Admin / Inventory Manager.</p>
        </div>
        {canManage && <Button onClick={() => setCreating(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="audit-new-btn"><Plus className="w-4 h-4 mr-1" /> New Audit</Button>}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all">All ({audits.length})</TabsTrigger>
          <TabsTrigger value="draft">Draft ({audits.filter(a => a.status === 'draft').length})</TabsTrigger>
          <TabsTrigger value="in_progress">In Progress ({audits.filter(a => a.status === 'in_progress').length})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({audits.filter(a => a.status === 'completed').length})</TabsTrigger>
          <TabsTrigger value="locked">Locked ({audits.filter(a => a.status === 'locked').length})</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <Card className="industrial-card">
            <CardContent className="p-0">
              {filtered.length === 0 ? (
                <div className="p-12 text-center text-slate-500"><ClipboardCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">No audits</p></div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Audit #</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(a => (
                      <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                        <TableCell><button onClick={() => openDetail(a.id)} className="mono text-orange-600 hover:underline">{a.audit_number}</button></TableCell>
                        <TableCell className="font-medium">{a.title}</TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {a.scope_categories?.length ? `${a.scope_categories.length} cat` : ''}
                          {a.scope_location_ids?.length ? ` ${a.scope_location_ids.length} loc` : ''}
                          {a.scope_item_ids?.length ? ` ${a.scope_item_ids.length} item` : ''}
                          {!(a.scope_categories?.length || a.scope_location_ids?.length || a.scope_item_ids?.length) && 'All items'}
                        </TableCell>
                        <TableCell><span className={`text-xs px-2 py-0.5 rounded-sm ${STATUS_COLOR[a.status]}`}>{a.status}</span></TableCell>
                        <TableCell className="text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString()}<br /><span className="text-[10px]">{a.created_by_name}</span></TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => openDetail(a.id)} data-testid={`audit-open-${a.id}`}><ChevronRight className="w-4 h-4" /></Button>
                          {a.status === 'draft' && canManage && <Button size="sm" variant="ghost" onClick={() => deleteAudit(a)} className="text-red-600" data-testid={`audit-delete-${a.id}`}><Trash2 className="w-4 h-4" /></Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="audit-create-dialog">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">New Audit</h2>
              <Button variant="ghost" size="sm" onClick={() => setCreating(false)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-5 space-y-4">
              <div><Label>Title *</Label><Input value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} className="rounded-sm" placeholder="Q1 Cycle Reconciliation" data-testid="audit-form-title" /></div>
              <div><Label>Description</Label><Textarea value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} rows={2} className="rounded-sm" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start Date</Label><Input type="date" value={createForm.start_date} onChange={(e) => setCreateForm({ ...createForm, start_date: e.target.value })} className="rounded-sm mono" /></div>
                <div><Label>End Date</Label><Input type="date" value={createForm.end_date} onChange={(e) => setCreateForm({ ...createForm, end_date: e.target.value })} className="rounded-sm mono" /></div>
              </div>
              {categories.length > 0 && (
                <div>
                  <Label>Scope by Category (optional)</Label>
                  <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto p-2 border border-slate-200 rounded-sm">
                    {categories.map(c => (
                      <label key={c.category_name} className="flex items-center gap-1 text-xs cursor-pointer">
                        <input type="checkbox" checked={createForm.scope_categories.includes(c.category_name)} onChange={() => toggleScopeCategory(c.category_name)} className="accent-orange-500" />
                        <span>{c.category_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {locations.length > 0 && (
                <div>
                  <Label>Scope by Location (optional)</Label>
                  <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto p-2 border border-slate-200 rounded-sm">
                    {locations.map(l => (
                      <label key={l.id} className="flex items-center gap-1 text-xs cursor-pointer">
                        <input type="checkbox" checked={createForm.scope_location_ids.includes(l.id)} onChange={() => toggleScopeLocation(l.id)} className="accent-orange-500" />
                        <span>{l.location_name} / {l.bin_id}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-slate-500 italic">If no scope is selected, the audit covers ALL active items.</p>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
              <Button onClick={create} className="bg-slate-900 hover:bg-slate-800" data-testid="audit-form-submit">Create</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditsPage;
